
import { NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {OrganizationChartModule} from 'primeng/organizationchart';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatMenuModule } from '@angular/material/menu';
import {MatIconModule} from '@angular/material/icon';

import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';

import { SharedModule } from '../shared/shared.module';

import { FooterComponent } from '../main-layout/footer/footer.component';
import { LoginComponent } from './pages/login/login.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';

import { VerifyEmailComponent } from './verify-email/verify-email.component';
import { LockComponent } from './pages/lock/lock.component';
import { PricingComponent } from './pages/pricing/pricing.component';
import { StatsCardComponent } from './dashboards/common/stats-card/stats-card.component';
import { StatsCard2Component } from './dashboards/common/stats-card2/stats-card2.component';
import { Dashboard3Component } from './dashboards/dashboard3/dashboard3.component';
import { PostListingComponent } from './pages/post-listing/post-listing.component';
import { UsersComponent } from './manage/users/users.component';
import { TeamsComponent } from './manage/teams/teams.component';
import { ContactsComponent } from './manage/contacts/contacts.component';
import { ContactFormComponent } from './manage/contacts/contact-form/contact-form.component';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { NgSelectModule } from '@ng-select/ng-select';
import { TemplatesComponent } from './workflows/templates/templates.component';
import { NewWorkflowTemplateComponent } from './workflows/new-workflow-template/new-workflow-template.component';
import { FileExplorerComponent } from './pages/file-explorer/file-explorer.component';
import { WFGeneralSettingsComponent } from './workflows/new-workflow-template/general-settings/general-settings.component';
import { WFMaterialsComponent } from './workflows/new-workflow-template/materials/materials.component';
import { WorkflowRulesComponent } from './workflows/new-workflow-template/workflow-rules/workflow-rules.component';
import { ManageFilesComponent } from './workflows/new-workflow-template/manage-files/manage-files.component';
import { SaveRulesComponent } from './workflows/new-workflow-template/save-rules/save-rules.component';
import { EmailRulesComponent } from './workflows/new-workflow-template/email-rules/email-rules.component';
import { AddEmailRuleComponent } from './workflows/new-workflow-template/add-email-rule/add-email-rule.component';
import { AddSaveRuleComponent } from './workflows/new-workflow-template/add-save-rule/add-save-rule.component';
import {EditorModule} from 'primeng/editor';
import { StorageComponent } from './manage/storage/storage.component';
import { AddCloudStorageComponent } from './manage/storage/add-cloud-storage/add-cloud-storage.component';
import { AddWebDavStorageComponent } from './manage/storage/add-webdav-storage/add-webdav-storage.component';
import { AddAmazonStorageComponent } from './manage/storage/add-amazon-storage/add-amazon-storage.component';
import { AddFTPStorageComponent } from './manage/storage/add-ftpstorage/add-ftpstorage.component';
import { AddSFTPStorageComponent } from './manage/storage/add-sftpstorage/add-sftpstorage.component';
import { CustomStepComponent } from './workflows/new-workflow-template/custom-step/custom-step.component';
import { CustomAutomatedActionsComponent } from './workflows/new-workflow-template/custom-automated-actions/custom-automated-actions.component';
import { WorkflowStatusComponent } from './workflows/workflow-status/workflow-status.component';
import { WorkflowStatusInfoComponent } from './workflows/workflow-status/workflow-status-info/workflow-status-info.component';
import { MultiStepsTreeComponent } from './workflows/new-workflow-template/multi-steps-tree/multi-steps-tree.component';
import { AddNewStepModalComponent } from './workflows/new-workflow-template/multi-steps-tree/add-new-step-modal/add-new-step-modal.component';
import { ShareComponent } from './pages/file-explorer/share/share.component';
import { FilesFoldersPickerComponent } from '../shared/components/files-folders-picker/files-folders-picker.component';
import { WorkflowTemplatesComponent } from './workflows/workflow-execution/workflow-templates/workflow-templates.component';
import { StepDetailsComponent } from './workflows/new-workflow-template/multi-steps-tree/step-details/step-details.component';
import { StepCompletionDetailsComponent } from './workflows/new-workflow-template/multi-steps-tree/step-completion-details/step-completion-details.component';
import { InitStepDetailsComponent } from './workflows/new-workflow-template/multi-steps-tree/init-step-details/init-step-details.component';
import { StorageChooserComponent } from './manage/storage/storage-chooser/storage-chooser.component';
import { AuthorizeComponent } from './accounts/authorize/authorize.component';
import { PoliciesPermissionsComponent } from './policies-permissions/policies-permissions.component';
import { AddCustomPolicyComponent } from './policies-permissions/add-custom-policy/add-custom-policy.component';
import { PolicyRulesComponent } from './policies-permissions/policy-rules/policy-rules.component';
import { SearchComponent } from './pages/search/search.component';
import { FieldsPickerComponent } from './fields-picker/fields-picker.component';
import { FormFillerComponent } from './form-filler/form-filler.component';
import { SettingsComponent } from './settings/settings.component';
import { ManageFormsComponent } from './workflows/new-workflow-template/manage-forms/manage-forms.component';
import { InputTokensComponent } from '../shared/components/input-tokens/input-tokens.component';
import { PrefixSuffixBuilderComponent } from './prefix-suffix-builder/prefix-suffix-builder.component';
import { AddEditWorkflowFileComponent } from './workflows/new-workflow-template/add-edit-workflow-file/add-edit-workflow-file.component';
import { AddEditWorkflowFormComponent } from './workflows/new-workflow-template/add-edit-workflow-form/add-edit-workflow-form.component';
import { FormsExplorerComponent } from './forms-explorer/forms-explorer.component';
import { BasenamePipe } from 'app/helpers/basename.pipe';
import { FormBuilderComponent } from './form-builder/form-builder.component';
import { FormDataComponent } from './form-data/form-data.component';
import { InitialSubmissionComponent } from './workflows/workflow-execution/initial-submission/initial-submission.component';
import { StepCompletionExecutionComponent } from './workflows/workflow-execution/step-completion-execution/step-completion-execution.component';
import { CompleteWorkflowTaskComponent } from './workflows/workflow-execution/complete-workflow-task/complete-workflow-task.component';
import { WorkflowExecMaterialsDetailsComponent } from './workflows/workflow-execution/workflow-exec-materials-details/workflow-exec-materials-details.component';
import { TimeAgoPipe } from 'app/helpers/time-ago.pipe';
import { ExternalCompleteWorkflowTaskComponent } from './workflows/workflow-execution/external-complete-workflow-task/external-complete-workflow-task.component';
import { HomeComponent } from './home/home.component';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { MyTasksComponent } from './my-tasks/my-tasks.component';
import { DashboardsComponent } from './manage/dashboards/dashboards.component';
import { FormComponent } from './gen/form/form.component';
import { CronEditorModule } from '@zapinfo/ngx-cron-editor';

import { WorkflowExecVariableComponent } from './workflow-exec-variable/workflow-exec-variable.component';
import { FieldsPickerModalComponent } from './fields-picker-modal/fields-picker-modal.component';
import { ReportsComponent } from './manage/reports/reports.component';
import { AddEditReportComponent } from './manage/reports/add-edit-report/add-edit-report.component';
import { AddEditReportAutoUploadComponent } from './manage/reports/add-edit-report-auto-upload/add-edit-report-auto-upload.component';
import { MetadataBuilderComponent } from './metadata-builder/metadata-builder.component';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { WopiComponent } from './wopi/wopi.component';
import { PdfEditComponent } from './pdf-edit/pdf-edit.component';
import { DeletedFormsComponent } from './deleted-forms/deleted-forms.component';
import { DirnamePipe } from 'app/helpers/dirname.pipe';
import { DeletedFilesComponent } from './deleted-files/deleted-files.component';
import { SpreadsheetToFormComponent } from './spreadsheet-to-form/spreadsheet-to-form.component';
import { OwlDateTimeModule, OwlNativeDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import { AutoStartRulesComponent } from './workflows/new-workflow-template/auto-start-rules/auto-start-rules.component';
import { AddEditAutoStartRuleComponent } from './workflows/new-workflow-template/add-edit-auto-start-rule/add-edit-auto-start-rule.component';
import { AddEditAutoStartFileComponent } from './workflows/new-workflow-template/add-edit-auto-start-file/add-edit-auto-start-file.component';
import { AddEditAutoStartFormComponent } from './workflows/new-workflow-template/add-edit-auto-start-form/add-edit-auto-start-form.component';
import { ManageConnectedAppsComponent } from './connected-apps/manage-connected-apps/manage-connected-apps.component';
import { AddEditConnectedAppComponent } from './connected-apps/add-edit-connected-app/add-edit-connected-app.component';
import { ConnectedAppAuthorizeComponent } from './connected-apps/connected-app-authorize/connected-app-authorize.component';
import { ConnectedAppRedirectComponent } from './connected-apps/connected-app-redirect/connected-app-redirect.component';
import { AddEditExternalOperationCommonSettingsComponent } from './connected-apps/add-edit-external-operation-common-settings/add-edit-external-operation-common-settings.component';
import { AceEditorModule } from 'ngx-ace-editor-wrapper';
import { MetadataEditorComponent } from './metadata-editor/metadata-editor.component';
import { MetadataEditorModalComponent } from './metadata-editor-modal/metadata-editor-modal.component';
import { LogoutComponent } from './logout/logout.component';
import { MetadataComponent } from './manage/metadata/metadata.component';
import { StepCompletionSelectorComponent } from './workflows/new-workflow-template/step-completion-selector/step-completion-selector.component';
import { DeleteStepCompletionComponent } from './workflows/new-workflow-template/delete-step-completion/delete-step-completion.component';
import { DeleteStepComponent } from './workflows/new-workflow-template/delete-step/delete-step.component';
import { CloneStepCompletionComponent } from './workflows/new-workflow-template/clone-step-completion/clone-step-completion.component';
import { DataImportsComponent } from './data-imports/data-imports.component';
import { SnakecasePipe } from 'app/helpers/snakecase.pipe';
import { SupportSsoComponent } from './accounts/support-sso/support-sso.component';
import { SubscriptionBillingComponent } from './subscription-billing/subscription-billing.component';
import { BillingSucceededComponent } from './billing-succeeded/billing-succeeded.component';
import { FeatureTrialLockModalComponent } from './feature-trial-lock-modal/feature-trial-lock-modal.component';
import { RegistrationProgressComponent } from './registration-progress/registration-progress.component';
import { SuperScriptsComponent } from './super-scripts/super-scripts.component';
import { SuperScriptModalComponent } from './super-scripts/super-script-modal/super-script-modal.component';
import { ConnectedEmailsComponent } from './connected-emails/connected-emails.component';
import { ConnectedEmailRedirectComponent } from './connected-emails/connected-email-redirect/connected-email-redirect.component';
import { EmailTriggersComponent } from './email-triggers/email-triggers.component';
import { WorkflowTriggersComponent } from './workflow-triggers/workflow-triggers.component';

@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserModule,
    BrowserAnimationsModule,
    SharedModule,
    NgxDatatableModule,
    DragDropModule,
    CalendarModule.forRoot({
      provide: DateAdapter,
      useFactory: adapterFactory
    }),
    NgSelectModule,
    EditorModule,
    OrganizationChartModule,
    NgxDaterangepickerMd.forRoot(),
    CronEditorModule,
    NgxSliderModule,
    OwlDateTimeModule, 
    OwlNativeDateTimeModule,
    AceEditorModule,
    MatMenuModule,
    MatIconModule
  ],
  declarations: [
    FooterComponent,
    LoginComponent,
    ResetPasswordComponent,
    VerifyEmailComponent,
    LockComponent,
    PricingComponent,
    StatsCardComponent,
    StatsCard2Component,
    Dashboard3Component,
    PostListingComponent,
    UsersComponent,
    TeamsComponent,
    ContactsComponent,
    ContactFormComponent,
    TemplatesComponent,
    NewWorkflowTemplateComponent,
    FileExplorerComponent,
    WFGeneralSettingsComponent,
    WFMaterialsComponent,
    WorkflowRulesComponent,
    ManageFilesComponent,
    SaveRulesComponent,
    EmailRulesComponent,
    AddEmailRuleComponent,
    AddSaveRuleComponent,
    StorageComponent,
    AddCloudStorageComponent,
    AddWebDavStorageComponent,
    AddAmazonStorageComponent,
    AddFTPStorageComponent,
    AddSFTPStorageComponent,
    WorkflowStatusComponent,
    WorkflowStatusInfoComponent,
    CustomStepComponent,
    CustomAutomatedActionsComponent,
    MultiStepsTreeComponent,
    AddNewStepModalComponent,
    ShareComponent,
    FilesFoldersPickerComponent,

    WorkflowTemplatesComponent,
    StepDetailsComponent,
    StepCompletionDetailsComponent,
    InitStepDetailsComponent,
    StorageChooserComponent,
    AuthorizeComponent,
    PoliciesPermissionsComponent,
    AddCustomPolicyComponent,
    PolicyRulesComponent,
    SearchComponent,
    FieldsPickerComponent,
    FormFillerComponent,
    SettingsComponent,
    ManageFormsComponent,
    InputTokensComponent,
    PrefixSuffixBuilderComponent,
    AddEditWorkflowFileComponent,
    AddEditWorkflowFormComponent,
    FormsExplorerComponent,
    BasenamePipe,
    DirnamePipe,
    SnakecasePipe,
    FormBuilderComponent,
    FormDataComponent,
    InitialSubmissionComponent,
    StepCompletionExecutionComponent,
    CompleteWorkflowTaskComponent,
    WorkflowExecMaterialsDetailsComponent,
    TimeAgoPipe,
    ExternalCompleteWorkflowTaskComponent,
    HomeComponent,
    MyTasksComponent,
    DashboardsComponent,
    FormComponent,
    WorkflowExecVariableComponent,
    FieldsPickerModalComponent,
    ReportsComponent,
    AddEditReportComponent,
    AddEditReportAutoUploadComponent,
    MetadataBuilderComponent,
    WopiComponent,
    PdfEditComponent,
    DeletedFormsComponent,
    DeletedFilesComponent,
    SpreadsheetToFormComponent,
    AutoStartRulesComponent,
    AddEditAutoStartRuleComponent,
    AddEditAutoStartFileComponent,
    AddEditAutoStartFormComponent,
    ManageConnectedAppsComponent,
    AddEditConnectedAppComponent,
    ConnectedAppAuthorizeComponent,
    ConnectedAppRedirectComponent,
    AddEditExternalOperationCommonSettingsComponent,
    MetadataEditorComponent,
    MetadataEditorModalComponent,
    LogoutComponent,
    MetadataComponent,
    StepCompletionSelectorComponent,
    DeleteStepCompletionComponent,
    DeleteStepComponent,
    CloneStepCompletionComponent,
    DataImportsComponent,
    SupportSsoComponent,
    SubscriptionBillingComponent,
    BillingSucceededComponent,
    FeatureTrialLockModalComponent,
    RegistrationProgressComponent,
    SuperScriptsComponent,
    SuperScriptModalComponent,
    ConnectedEmailsComponent,
    ConnectedEmailRedirectComponent,
    EmailTriggersComponent,
    WorkflowTriggersComponent
  ],
  exports: [
    FooterComponent,
    LoginComponent,
    ResetPasswordComponent,
    VerifyEmailComponent,
    LockComponent,
    PricingComponent,
    StatsCardComponent,
    StatsCard2Component,    
    Dashboard3Component,
    PostListingComponent,
  ],
  schemas: [NO_ERRORS_SCHEMA]
})
export class ViewsModule { }
